import { axiosInstance } from "@refinedev/simple-rest";

import type {
  MoodChatRunDetail,
  MoodChatRunFilters,
  MoodChatRunSummary,
  MoodChatRunsResponse,
} from "./types";

/** The backend clamps `limit` to 1..100, so this is the largest useful page. */
const EXPORT_PAGE_SIZE = 100;

/** 10,000 runs — enough for any real range, and a backstop against a stray decade. */
const DEFAULT_MAX_PAGES = 100;

type ExportPageFetcher = (
  apiUrl: string,
  filters: MoodChatRunFilters,
) => Promise<MoodChatRunsResponse>;

export interface ExportMoodChatLogRunsOptions {
  onProgress?: (loaded: number) => void;
  maxPages?: number;
  /** Injectable for tests; defaults to the real endpoint. */
  fetchPage?: ExportPageFetcher;
}

export interface ExportMoodChatLogRunsResult {
  runs: MoodChatRunSummary[];
  truncated: boolean;
}

/**
 * Collects every run matching `filters` by following `nextPageToken`. Paging has to
 * be sequential — the token is opaque, so pages cannot be fetched in parallel.
 */
export async function exportMoodChatLogRuns(
  apiUrl: string,
  filters: MoodChatRunFilters,
  options: ExportMoodChatLogRunsOptions = {},
): Promise<ExportMoodChatLogRunsResult> {
  const {
    onProgress,
    maxPages = DEFAULT_MAX_PAGES,
    fetchPage = fetchMoodChatLogRuns,
  } = options;

  const runs: MoodChatRunSummary[] = [];
  let pageToken: string | undefined;

  for (let page = 0; page < maxPages; page += 1) {
    const response = await fetchPage(apiUrl, {
      ...filters,
      limit: EXPORT_PAGE_SIZE,
      pageToken,
    });

    runs.push(...response.data);
    onProgress?.(runs.length);

    if (!response.nextPageToken) {
      return { runs, truncated: false };
    }

    pageToken = response.nextPageToken;
  }

  return { runs, truncated: true };
}

export async function fetchMoodChatLogRuns(
  apiUrl: string,
  filters: MoodChatRunFilters,
): Promise<MoodChatRunsResponse> {
  const response = await axiosInstance.get(`${apiUrl}/mood-chat-logs/runs`, {
    params: {
      "api-version": "1.0",
      ...compact(filters),
    },
  });

  return response.data;
}

export async function fetchMoodChatLogRun(
  apiUrl: string,
  runId: string,
  window?: Pick<MoodChatRunFilters, "from" | "to">,
): Promise<MoodChatRunDetail> {
  const response = await axiosInstance.get(
    `${apiUrl}/mood-chat-logs/runs/${runId}`,
    {
      params: {
        "api-version": "1.0",
        ...compact(window ?? {}),
      },
    },
  );

  return response.data;
}

function compact<T extends Record<string, unknown>>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(
      ([, item]) => item !== undefined && item !== "",
    ),
  ) as Partial<T>;
}
